import { CommonModule, DatePipe } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";

// PrimeNG imports
import { MenuItem } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { Menu, MenuModule } from "primeng/menu";
import { TableModule, TableLazyLoadEvent } from "primeng/table";
import { TagModule } from "primeng/tag";
import { TooltipModule } from "primeng/tooltip";

// Shared components
import {
  TableSkeletonRowsComponent,
  SKELETON_PRESETS,
} from "../../../../shared/components/table-skeleton";

// Models
import {
  getPaymentMethodDisplayName,
  PaymentIntegration,
  PaymentIntegrationListResponse,
} from "../../models/payment-integration.model";

/**
 * Row action types
 */
export type PaymentIntegrationRowAction = "edit" | "delete";

/**
 * Sort event interface
 */
export interface SortEvent {
  field: string;
  order: 1 | -1;
}

/**
 * Page event interface
 */
export interface PageEvent {
  first: number;
  rows: number;
  page?: number;
  pageCount?: number;
}

@Component({
  selector: "app-payment-integrations-table",
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    ButtonModule,
    MenuModule,
    TableModule,
    TagModule,
    TooltipModule,
    TableSkeletonRowsComponent,
  ],
  templateUrl: "./payment-integrations-table.component.html",
  styleUrl: "./payment-integrations-table.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentIntegrationsTableComponent {
  @Input() data: PaymentIntegrationListResponse | null = null;
  @Input() loading = false;
  @Input() scrollHeight = "flex";

  @Output() sortChanged = new EventEmitter<SortEvent>();
  @Output() pageChanged = new EventEmitter<PageEvent>();
  @Output() rowAction = new EventEmitter<{
    action: PaymentIntegrationRowAction;
    item: PaymentIntegration;
  }>();

  @ViewChild("contextMenu") contextMenu!: Menu;

  // Selected item for context menu actions
  private selectedItem: PaymentIntegration | null = null;

  // Context menu items
  contextMenuItems: MenuItem[] = [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: () => {
        if (this.selectedItem) {
          this.onEdit(this.selectedItem);
        }
      },
    },
    {
      separator: true,
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      styleClass: "menu-item-danger",
      command: () => {
        if (this.selectedItem) {
          this.onDelete(this.selectedItem);
        }
      },
    },
  ];

  // Use shared skeleton preset for consistent loading appearance
  readonly skeletonColumns = SKELETON_PRESETS.paymentIntegrations;

  // Current sort state
  sortField = "UpdatedUtc";
  sortOrder: 1 | -1 = -1;

  /**
   * Get items from data response
   */
  get items(): PaymentIntegration[] {
    return this.data?.Items ?? [];
  }

  /**
   * Get total records count
   */
  get totalRecords(): number {
    return this.data?.TotalCount ?? 0;
  }

  /**
   * Get current page size
   */
  get pageSize(): number {
    return this.data?.PageSize ?? 25;
  }

  /**
   * Get current first row index (for paginator)
   */
  get first(): number {
    const page = this.data?.Page ?? 1;
    return (page - 1) * this.pageSize;
  }

  /**
   * Get display name for payment method type
   */
  getProviderDisplayName(type: string): string {
    return getPaymentMethodDisplayName(type);
  }

  /**
   * Mask sensitive value for display
   */
  maskValue(value: string | null): string {
    if (!value) return "—";
    if (value.length <= 4) return "****";
    return "****" + value.slice(-4);
  }

  /**
   * Get test mode severity for tag
   */
  getTestModeSeverity(testMode: boolean): "success" | "warn" {
    return testMode ? "warn" : "success";
  }

  /**
   * Get test mode label
   */
  getTestModeLabel(testMode: boolean): string {
    return testMode ? "Test" : "Live";
  }

  /**
   * Handle lazy load event (pagination/sorting)
   */
  onLazyLoad(event: TableLazyLoadEvent): void {
    // Handle sort
    if (event.sortField && typeof event.sortField === "string") {
      this.sortField = event.sortField;
      this.sortOrder = (event.sortOrder as 1 | -1) ?? 1;
      this.sortChanged.emit({
        field: this.sortField,
        order: this.sortOrder,
      });
    }

    // Handle pagination
    if (
      event.first !== undefined &&
      event.rows !== undefined &&
      event.rows !== null
    ) {
      const rows = event.rows;
      this.pageChanged.emit({
        first: event.first,
        rows: rows,
        page: Math.floor(event.first / rows),
      });
    }
  }

  /**
   * Handle page change from paginator
   */
  onPageChange(event: PageEvent): void {
    this.pageChanged.emit(event);
  }

  /**
   * Toggle the row context menu
   */
  toggleRowMenu(event: Event, item: PaymentIntegration): void {
    this.selectedItem = item;
    this.contextMenu.toggle(event);
    event.stopPropagation();
  }

  /**
   * Handle ID link click - navigates to edit page
   */
  onIdClick(item: PaymentIntegration): void {
    this.rowAction.emit({ action: "edit", item });
  }

  /**
   * Handle edit action
   */
  onEdit(item: PaymentIntegration): void {
    this.rowAction.emit({ action: "edit", item });
  }

  /**
   * Handle delete action
   */
  onDelete(item: PaymentIntegration): void {
    this.rowAction.emit({ action: "delete", item });
  }

  /**
   * TrackBy function for table rows
   */
  trackById(_index: number, item: PaymentIntegration): number {
    return item.Id;
  }
}
